import { Component, computed, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { MatIconButton } from '@angular/material/button';
import { DeviceService, IconComponent } from '@gains-com/ui-angular';
import { PromotionRewardType } from '@gains-com/shared';
import { PromotionsView } from '../../core/view-states/promotions-view.service';
import { UserViewService } from '../../core/view-states/user-view.service';
import { LocalStorage } from '../../core/local-storage/local-storage.service';
import { LocalStorageKey } from '../../core/local-storage/local-storage-keys';
import { merchBoostConfig } from '../../core/merch-boost';
import { merchDiscountConfig } from '../../core/merch-discount';

@Component({
  selector: 'app-promo-banner',
  standalone: true,
  imports: [IconComponent, MatIconButton, RouterLink],
  templateUrl: './promo-banner.component.html',
  styleUrl: './promo-banner.component.scss',
})
export class PromoBannerComponent {
  private readonly _promotionsView = inject(PromotionsView);
  private readonly _userView = inject(UserViewService);
  private readonly _localStorage = inject(LocalStorage);
  private readonly _router = inject(Router);
  private readonly _platformId = inject(PLATFORM_ID);
  readonly deviceService = inject(DeviceService);

  // User state
  private readonly _user = toSignal(this._userView.state$);

  // Convert promotions count to signal for computed dependencies and template access
  readonly promotionsCount = computed(() => this.activePromos().length);
  readonly promotions = toSignal(this._promotionsView.state$);

  // Bumped on dismissal to force the dismissed-IDs computed to re-read storage.
  private readonly _dismissalTick = signal(0);

  // Dismissed promotion IDs, derived from local storage (SSR-safe via the
  // LocalStorage service, which returns null on the server). Recomputes when
  // the user / active-promo count changes, or after a dismissal.
  private readonly _dismissedPromoIds = computed<string[] | null>(() => {
    this._dismissalTick();

    const user = this._user();
    const count = this.promotionsCount();

    if (user && count > 0) {
      return this._localStorage.getItem<string[]>(LocalStorageKey.GainsPromoBannerDismissed);
    }

    return null;
  });

  activePromos = computed(() => this.promotions()?.promotions ?? []);

  // A first_time_merch_discount arrives already claimed at signup, so its mere
  // presence among the active promos means the discount is live at checkout.
  // Unusable metadata fails closed to the generic banner, matching the shop
  // surfaces (merchDiscountConfig returns null).
  private readonly _firstTimeMerchDiscount = computed(() => {
    for (const promo of this.activePromos()) {
      const reward = promo.rewards?.find(
        (r) => r.promotion_reward_type_id === PromotionRewardType.FirstTimeMerchDiscount,
      );
      const config = merchDiscountConfig(reward);
      if (config) {
        return config;
      }
    }
    return null;
  });

  // The welcome offer is auto-claimed at grant the same way; it displaces the
  // discount copy because checkout applies a boost claim first.
  private readonly _firstTimeMerchBoost = computed(() => {
    for (const promo of this.activePromos()) {
      const reward = promo.rewards?.find(
        (r) => r.promotion_reward_type_id === PromotionRewardType.FirstTimeMerchPurchaseOffer,
      );
      const config = merchBoostConfig(reward);
      if (config) {
        return config;
      }
    }
    return null;
  });

  readonly bannerLink = computed(() =>
    this._firstTimeMerchBoost() || this._firstTimeMerchDiscount() ? '/shop' : '/promotions',
  );

  readonly bannerText = computed(() => {
    const boost = this._firstTimeMerchBoost();
    if (boost) {
      return `Up to ${boost.topBonusSc} bonus Sweeps Coins on your first order — on selected products`;
    }
    const discount = this._firstTimeMerchDiscount();
    return discount
      ? `You have ${discount.percent}% off your first order`
      : `New Promo${this.promotionsCount() > 1 ? 's' : ''} Available`;
  });

  // Determine if banner should be shown
  shouldShowBanner = computed(() => {
    // Don't render on the server to prevent hydration mismatch
    if (!isPlatformBrowser(this._platformId)) {
      return false;
    }

    // Only show on mobile (xs screen)
    if (!this.deviceService.isXsScreen()) {
      return false;
    }

    // Don't show if user is not logged in
    const user = this._user();
    if (!user) {
      return false;
    }

    // Don't show if user has promotions disabled
    if (!user.promotions_enabled) {
      return false;
    }

    const activePromos = this.activePromos();
    const currentCount = activePromos.length;

    // Don't show if no promotions
    if (currentCount <= 0) {
      return false;
    }

    // Check if banner was previously dismissed (now reactive)
    const dismissedIds = this._dismissedPromoIds();

    return (
      !dismissedIds ||
      dismissedIds.sort().join() !==
        activePromos
          .map((p) => p.user_promotion_id)
          .sort()
          .join()
    );
  });

  async onDismiss(event: Event) {
    event.stopPropagation();

    const dismissedIds = this.activePromos().map((p) => p.user_promotion_id);

    this._localStorage.setItem<string[]>(LocalStorageKey.GainsPromoBannerDismissed, dismissedIds);

    // Force the dismissed-IDs computed to re-read storage so the banner hides.
    this._dismissalTick.update((tick) => tick + 1);
  }
}
